import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Answers one question: has a bank feed actually delivered anything, and how recently?
 *
 * WHY THIS DOES NOT LOOK AT TRANSACTIONS OR ACCOUNTS
 *
 * The obvious signals are both contaminated. `bank_transactions.date` moves whenever
 * somebody uploads an XML statement, and Xero writes the same table; so does
 * `bank_accounts.last_synced_at` and `sync_status`, which StatementUploader stamps on
 * every import. Deriving feed health from either means one manual upload silently
 * reclassifies a dead feed as live — and on a multi-account dashboard a single fresh
 * row hides staleness across every other account.
 *
 * The only provenance a manual import cannot forge is the sync path's own record:
 * `user_integrations.last_sync_status`, written exclusively by
 * integrationService.syncIntegration alongside integration_sync_history. That is what
 * this reads, and nothing else.
 *
 * SELF-CLEARING BY CONSTRUCTION
 *
 * Because the same write that makes a sync succeed is the one this reads, a restored
 * feed clears the warning on its own. Nothing here is hardcoded to a provider or a
 * verdict; connect Revolut, Brex or Mercury and have it sync once and this reports
 * `live` without a code change.
 *
 * SCOPE IS THE VIEWING USER, DELIBERATELY
 *
 * RLS on user_integrations is `auth.uid() = user_id`, so a user can only ever see their
 * own connections. The copy therefore speaks about this account's connections rather
 * than claiming anything about the organisation's.
 */

/** Below this, a feed that has synced is behaving normally and no notice is warranted. */
export const STALE_AFTER_DAYS = 7;

/** Feed health is a claim about the SYNC PATH. Never derive it from row contents. */
export type BankFeedHealth =
  | { kind: 'checking' }
  /** The check itself failed. Not evidence of anything; say nothing. */
  | { kind: 'unknown' }
  | { kind: 'no-connections' }
  | { kind: 'never-synced'; providers: string[] }
  | { kind: 'stale'; providers: string[]; days: number }
  | { kind: 'live'; providers: string[]; days: number };

export interface BankFeedStatus {
  health: BankFeedHealth;
  /**
   * How old the figures on screen are, whatever put them there.
   *
   * Descriptive only — it answers "how current is what I am reading", which stays a fair
   * question for imported statements too. It must never feed the health verdict above;
   * that separation is the whole point of this module.
   */
  newestMovement: { date: string; days: number } | null;
}

/** Exactly the columns the sync path writes. Nothing a manual import can touch. */
export interface BankingConnectionRow {
  is_active: boolean | null;
  provider: string | null;
  last_sync_status: string | null;
  last_synced_at: string | null;
}

const DAY_MS = 86_400_000;

/** Cheap re-read window. The nav mounts on every page; feed health moves in hours. */
const CACHE_TTL_MS = 120_000;

let cache: { at: number; value: Promise<BankFeedStatus> } | null = null;
const healthListeners = new Set<() => void>();

/** Refresh mounted readers after a confirmed connection lifecycle change. */
export function invalidateBankFeedHealth() {
  cache = null;
  healthListeners.forEach(refresh => refresh());
}

function daysSince(iso: string, now: number): number {
  return Math.floor((now - new Date(iso).getTime()) / DAY_MS);
}

/**
 * The single staleness boundary, shared with BankGroupCard.
 *
 * Exported because sharing STALE_AFTER_DAYS alone was not enough. This function floors
 * the age to whole days before comparing, so it turns stale at day 8; BankGroupCard first
 * compared raw milliseconds against the same constant and turned stale at day 7, leaving a
 * day in which a card rendered red while the nav badge still reported the feed live.
 * Raised in review on #714.
 *
 * Two callers agreeing because they call the same function cannot drift; two callers
 * agreeing because a test says they should, can.
 */
export function isStaleAge(ageMs: number): boolean {
  return Math.floor(ageMs / DAY_MS) > STALE_AFTER_DAYS;
}

/**
 * The whole verdict, as a pure function of the sync path's own record.
 *
 * Separated from the query so the rule can be tested without a database, and so it is
 * self-evident that no other input reaches it. `null` rows mean the read failed, which
 * is not evidence about the feed either way.
 */
export function deriveBankFeedHealth(
  rows: BankingConnectionRow[] | null,
  now: number = Date.now(),
): BankFeedHealth {
  if (rows === null) return { kind: 'unknown' };

  const activeRows = rows.filter(row => row.is_active === true);
  const providers = [...new Set(activeRows.map((r) => r.provider).filter((p): p is string => !!p))].sort();
  if (activeRows.length === 0) return { kind: 'no-connections' };

  // A stored connection_status of 'connected' only records that credentials were saved.
  // Only a completed sync is evidence that data has ever moved.
  const successTimes = activeRows
    .filter((r) => r.last_sync_status === 'success' && r.last_synced_at)
    .map((r) => new Date(r.last_synced_at as string).getTime())
    .filter((t) => Number.isFinite(t));

  if (successTimes.length === 0) return { kind: 'never-synced', providers };

  const ageMs = now - Math.max(...successTimes);
  const days = Math.floor(ageMs / DAY_MS);
  return isStaleAge(ageMs) ? { kind: 'stale', providers, days } : { kind: 'live', providers, days };
}

async function readBankFeedStatus(): Promise<BankFeedStatus> {
  // Two independent reads: provenance decides health, movement only describes age.
  const [connections, newest] = await Promise.all([
    supabase
      .from('user_integrations')
      .select('provider, is_active, last_sync_status, last_synced_at')
      .eq('category', 'banking')
      .eq('is_active', true),
    supabase
      .from('bank_transactions')
      .select('date')
      .order('date', { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  const now = Date.now();
  const newestMovement =
    !newest.error && newest.data?.date
      ? { date: newest.data.date as string, days: daysSince(newest.data.date as string, now) }
      : null;

  // Fail quiet: an unreadable check is not evidence the feed is broken, and a banner
  // that cries wolf gets ignored when it matters.
  const rows = connections.error ? null : ((connections.data ?? []) as BankingConnectionRow[]);
  return { health: deriveBankFeedHealth(rows, now), newestMovement };
}

/** What a nav entry should say about the feed, or null when there is nothing to say. */
export interface BankFeedBadge {
  /** Short enough for a nav pill. Distinguishes no feed from a feed that has gone quiet. */
  label: string;
  reason: string;
}

/**
 * Null for 'live', and equally for 'checking' and 'unknown'.
 *
 * Silence on an unresolved check is deliberate: a badge that appears for a moment on
 * every page load, or whenever the query fails, teaches people to ignore it.
 */
export function bankFeedNavBadge(health: BankFeedHealth): BankFeedBadge | null {
  switch (health.kind) {
    case 'no-connections':
      return {
        label: 'No feed',
        reason:
          'No bank feed is connected to this account — the figures here are historical records, not today’s position.',
      };
    case 'never-synced':
      return {
        label: 'No feed',
        reason: `A bank connection is registered (${health.providers.join(', ')}) but no sync has ever completed, so nothing here is live.`,
      };
    case 'stale':
      // Not 'No feed': this one is connected and has synced. Saying otherwise would be
      // the same kind of false claim the notice exists to stop.
      return {
        label: 'Stale',
        reason: `The last successful bank sync was ${health.days} days ago, so these figures are not today’s position.`,
      };
    default:
      return null;
  }
}

export function useBankFeedHealth(): BankFeedStatus {
  const [status, setStatus] = useState<BankFeedStatus>({ health: { kind: 'checking' }, newestMovement: null });

  useEffect(() => {
    let active = true;

    let readVersion = 0;
    const refresh = () => {
      const version = ++readVersion;
      if (!cache || Date.now() - cache.at > CACHE_TTL_MS) {
        cache = {
          at: Date.now(),
          value: readBankFeedStatus().catch(() => ({
            health: { kind: 'unknown' } as BankFeedHealth,
            newestMovement: null,
          })),
        };
      }
      setStatus(current => ({ ...current, health: { kind: 'checking' } }));
      void cache.value.then(value => {
        if (active && version === readVersion) setStatus(value);
      });
    };
    healthListeners.add(refresh);
    refresh();

    return () => {
      active = false;
      healthListeners.delete(refresh);
    };
  }, []);

  return status;
}

/** Test seam only — the module cache would otherwise leak one case into the next. */
export function __resetBankFeedHealthCache() {
  cache = null;
}
